// src/pages/affiliate/CommissionsPage.tsx
import React, { useState, useEffect } from 'react';
import { useUI } from '../../context/UIContext';
import { useAffiliate } from '../../context/AffiliateContext';
import { isPendingApprovalError } from '../../context/AffiliateContext';
import { getBaseUrl } from '../../config/api';
import PendingApprovalView from '../../components/affiliate/PendingApprovalView';
import '../../styles/AffiliateCommissions.css';
import { formatCommissionPercentForDisplay } from '../../utils/affiliateFormat';

interface Commission {
  _id: string;
  bookingId: {
    _id: string;
    service: string;
    totalPrice: number;
  };
  userId: {
    firstName: string;
    lastName: string;
  };
  commissionAmount: number;
  commissionRate: number;
  status: string;
  createdAt: string;
  approvedAt?: string;
  paidAt?: string;
}

interface CommissionSummary {
  totalCommissions: number;
  totalEarnings: number;
  pendingCommissions: number;
  approvedCommissions: number;
  paidCommissions: number;
}

const AffiliateCommissionsPage: React.FC = () => {
  const { showSnackbar } = useUI();
  const { isPending, setAffiliateStatus } = useAffiliate();
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [summary, setSummary] = useState<CommissionSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: 'all',
    sortBy: '-createdAt',
    limit: 20
  });

  useEffect(() => {
    if (isPending) return;
    fetchCommissions();
    fetchSummary();
  }, [currentPage, filters, isPending]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAuthError = (status: number, message?: string) => {
    if (status === 401) {
      localStorage.removeItem('affiliateToken');
      sessionStorage.removeItem('affiliateToken');
      sessionStorage.removeItem('affiliateUser');
      localStorage.removeItem('affiliateUser');
      setAffiliateStatus(null);
      showSnackbar('Session expired. Please log in again.', 'error');
      window.location.href = '/affiliate/login';
      return true;
    }
    if (status === 403 && isPendingApprovalError(status, message)) {
      setAffiliateStatus('pending');
      showSnackbar('Your account is pending approval. This page will be available once approved.', 'info');
      return true;
    }
    return false;
  };

  const fetchCommissions = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
      
      if (!token) {
        showSnackbar('Please log in to view commissions', 'error');
        return;
      }

      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: filters.limit.toString(),
        sort: filters.sortBy,
        ...(filters.status !== 'all' && { status: filters.status })
      });

      const response = await fetch(
        `${getBaseUrl()}/v1/affiliate/commissions?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setCommissions(data.data.commissions);
        setCurrentPage(data.data.currentPage);
        setTotalPages(data.data.totalPages);
      } else {
        if (handleAuthError(response.status, data.message)) return;
        showSnackbar(data.message || 'Failed to load commissions', 'error');
      }
    } catch (error) {
      console.error('Fetch commissions error:', error);
      showSnackbar('An error occurred loading commissions', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
      
      if (!token) return;

      const response = await fetch(
        `${getBaseUrl()}/v1/affiliate/commissions/summary`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setSummary(data.data);
      } else {
        handleAuthError(response.status, data.message);
      }
    } catch (error) {
      console.error('Fetch summary error:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'approved':
        return 'badge-success';
      case 'pending':
        return 'badge-warning';
      case 'paid':
        return 'badge-info';
      case 'cancelled':
        return 'badge-error';
      default:
        return 'badge-default';
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const [disputeModal, setDisputeModal] = useState<{
    isOpen: boolean;
    commissionId: string | null;
    reason: string;
  }>({
    isOpen: false,
    commissionId: null,
    reason: ''
  });

  const handleDispute = (commissionId: string) => {
    setDisputeModal({
      isOpen: true,
      commissionId,
      reason: ''
    });
  };

  const handleDisputeSubmit = async () => {
    if (!disputeModal.commissionId || disputeModal.reason.trim().length < 10) {
      showSnackbar('Please provide a detailed reason (minimum 10 characters)', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
      
      if (!token) {
        showSnackbar('Please log in to dispute a commission', 'error');
        return;
      }

      const response = await fetch(
        `${getBaseUrl()}/v1/affiliate/commissions/${disputeModal.commissionId}/dispute`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reason: disputeModal.reason.trim()
          })
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        showSnackbar('Dispute submitted successfully. Our team will review it.', 'success');
        setDisputeModal({ isOpen: false, commissionId: null, reason: '' });
        fetchCommissions(); // Refresh the list
      } else {
        if (handleAuthError(response.status, data.message)) return;
        showSnackbar(data.message || 'Failed to submit dispute', 'error');
      }
    } catch (error) {
      console.error('Dispute error:', error);
      showSnackbar('An error occurred submitting the dispute', 'error');
    }
  };

  if (isPending) {
    return <PendingApprovalView title="Commissions" />;
  }

  return (
    <div className="affiliate-commissions">
      <div className="affiliate-commissions__header">
        <div>
          <h1>Commission History</h1>
          <p>Track all your earned commissions</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="affiliate-commissions__summary">
          <div className="summary-card">
            <div className="summary-card__icon summary-card__icon--blue">
              <i className="fas fa-list"></i>
            </div>
            <div className="summary-card__content">
              <h3>Total Commissions</h3>
              <p className="summary-card__value">{summary.totalCommissions}</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card__icon summary-card__icon--purple">
              <i className="fas fa-dollar-sign"></i>
            </div>
            <div className="summary-card__content">
              <h3>Total Earnings</h3>
              <p className="summary-card__value">{formatCurrency(summary.totalEarnings)}</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card__icon summary-card__icon--orange">
              <i className="fas fa-clock"></i>
            </div>
            <div className="summary-card__content">
              <h3>Pending</h3>
              <p className="summary-card__value">{formatCurrency(summary.pendingCommissions)}</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card__icon summary-card__icon--green">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="summary-card__content">
              <h3>Paid Out</h3>
              <p className="summary-card__value">{formatCurrency(summary.paidCommissions)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="affiliate-commissions__filters">
        <div className="filter-group">
          <label htmlFor="status-filter">Status:</label>
          <select
            id="status-filter"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="sort-filter">Sort By:</label>
          <select
            id="sort-filter"
            value={filters.sortBy}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
          >
            <option value="-createdAt">Newest First</option>
            <option value="createdAt">Oldest First</option>
            <option value="-commissionAmount">Highest Amount</option>
            <option value="commissionAmount">Lowest Amount</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="limit-filter">Show:</label>
          <select
            id="limit-filter"
            value={filters.limit}
            onChange={(e) => handleFilterChange('limit', e.target.value)}
          >
            <option value="10">10 per page</option>
            <option value="20">20 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
        </div>
      </div>

      {/* Commissions Table */}
      {isLoading ? (
        <div className="affiliate-commissions__loading">
          <div className="spinner"></div>
          <p>Loading commissions...</p>
        </div>
      ) : commissions.length === 0 ? (
        <div className="affiliate-commissions__empty">
          <i className="fas fa-inbox"></i>
          <p>No commissions found</p>
          <small>Start promoting to earn your first commission!</small>
        </div>
      ) : (
        <>
          <div className="affiliate-commissions__table-container">
            <table className="affiliate-commissions__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Service</th>
                  <th>Booking Amount</th>
                  <th>Rate</th>
                  <th>Commission</th>
                  <th>Status</th>
                  <th>Paid Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((commission) => (
                  <tr key={commission._id}>
                    <td>{formatDate(commission.createdAt)}</td>
                    <td>
                      {commission.userId.firstName} {commission.userId.lastName}
                    </td>
                    <td>{commission.bookingId.service || 'N/A'}</td>
                    <td>{formatCurrency(commission.bookingId.totalPrice)}</td>
                    <td>{formatCommissionPercentForDisplay(commission.commissionRate)}</td>
                    <td className="amount">{formatCurrency(commission.commissionAmount)}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(commission.status)}`}>
                        {commission.status}
                      </span>
                    </td>
                    <td>
                      {commission.paidAt ? formatDate(commission.paidAt) : '-'}
                    </td>
                    <td>
                      {['pending', 'approved'].includes(commission.status) && (
                        <button
                          onClick={() => handleDispute(commission._id)}
                          className="btn-dispute"
                          title="Dispute this commission"
                        >
                          <i className="fas fa-exclamation-triangle"></i>
                          Dispute
                        </button>
                      )}
                      {commission.status === 'disputed' && (
                        <span className="dispute-badge">
                          <i className="fas fa-clock"></i>
                          Under Review
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="affiliate-commissions__pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                <i className="fas fa-chevron-left"></i>
                Previous
              </button>

              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {/* Dispute Modal */}
      {disputeModal.isOpen && (
        <div className="modal-overlay" onClick={() => setDisputeModal({ isOpen: false, commissionId: null, reason: '' })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Dispute Commission</h2>
              <button
                className="modal-close"
                onClick={() => setDisputeModal({ isOpen: false, commissionId: null, reason: '' })}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>Please provide a detailed reason for disputing this commission. Our team will review it within 2-3 business days.</p>
              <div className="form-group">
                <label htmlFor="dispute-reason">Dispute Reason *</label>
                <textarea
                  id="dispute-reason"
                  rows={5}
                  value={disputeModal.reason}
                  onChange={(e) => setDisputeModal({ ...disputeModal, reason: e.target.value })}
                  placeholder="Explain why you're disputing this commission. Include any relevant details, dates, or evidence..."
                  minLength={10}
                  required
                />
                <small>{disputeModal.reason.length}/10 characters (minimum 10 required)</small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setDisputeModal({ isOpen: false, commissionId: null, reason: '' })}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleDisputeSubmit}
                disabled={disputeModal.reason.trim().length < 10}
              >
                Submit Dispute
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateCommissionsPage;

